/**
 * Shared helpers for `{date: count}` jsonb maps (e.g. `events.meeting_room_counts_by_date`).
 * Consolidates what was previously 4 independent hand-rolled copies of this coercion across
 * the wizard write path, the event-details write path, the jsonb read path, and the RFP
 * email formatter — each with slightly different rules.
 */

/**
 * Coerce a jsonb value to a `{date: count}` record, dropping non-string keys and non-finite
 * values from legacy/malformed data. No allowlist/date-range filtering — for the read path,
 * where the value is display-only. For a write path that must validate against a set of
 * allowed dates, use {@link sanitizeDateCountMap} instead.
 *
 * @param value - Raw jsonb value from the events row.
 * @returns Date-count record (possibly empty).
 */
export function readDateCountMap(value: unknown): Record<string, number> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    const count = Number(raw);
    if (Number.isFinite(count)) result[key] = count;
  }
  return result;
}

/**
 * Formats a `YYYY-MM-DD` date string as "Saturday, Aug 15" — shared so the RFP email formatter
 * and the plain-text event-requirements summary render the same weekday/month/day for a given
 * date instead of hand-rolling the same `toLocaleDateString` call independently.
 *
 * @param dateStr - Date string in YYYY-MM-DD format.
 * @returns Formatted day-of-week date string.
 */
export function formatLongWeekdayDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

/**
 * Validates and clamps a client-supplied `{date: count}` map for persistence. Iterates the
 * ALLOWED dates and intersects with the payload — never the client-supplied keys — so a
 * crafted key (e.g. `__proto__`) can't reach the output, and out-of-range dates are silently
 * pruned rather than trusted. Each value is clamped to a non-negative integer within `maxValue`.
 *
 * @param raw - Client-supplied map (untrusted).
 * @param allowedDates - The set of dates this map is allowed to have entries for.
 * @param maxValue - Upper clamp for each count (e.g. `SMALLINT_MAX`).
 * @returns The validated, clamped map — only dates present in both `raw` and `allowedDates`.
 */
export function sanitizeDateCountMap(
  raw: Record<string, number>,
  allowedDates: ReadonlySet<string>,
  maxValue: number
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const date of allowedDates) {
    const value = Number(raw[date]);
    if (!Number.isFinite(value)) continue;
    result[date] = Math.min(Math.max(Math.floor(value), 0), maxValue);
  }
  return result;
}
